import { z } from "zod";
import { ConditionSchema } from "./conditions.ts";
import {
	UI_PROPERTY_KEYS,
	isValidUiPropertyKey,
	normalizeUiPropertyKey,
} from "./uiPropertyRegistry.ts";

const COMMENT_TEXT_MAX_LENGTH = 4000;
const COMMENT_SIZE_MAX = 4096;

export const EVENT_TYPES = [
	"OnSpawn",
	"OnTouch",
	"OnConsume",
	"OnDamage",
	"OnDeath",
	"OnCreate",
	"OnRespawn",
	"OnClick",
	"OnHover",
	"OnUnhover",
	"OnMouseDown",
	"OnMouseUp",
	"OnVisible",
	"OnHidden",
	"OnEnter",
	"OnExit",
	"OnZoneEnter",
	"OnZoneExit",
	"OnMatchReady",
	"OnMatchFailed",
	"OnTeamWin",
	"OnTeamDefeated",
	"OnMatchQuit",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];
export const EventTypeSchema = z.enum(EVENT_TYPES);

export const ACTION_TYPES = [
	"ModifyStat",
	"ModifyCurrency",
	"SetTimer",
	"ModifyItem",
	"AddRandomItem",
	"ExecuteTransaction",
	"ConsumeItem",
	"ToggleEquip",
	"DeployItem",
	"RecallItem",
	"PlaceItem",
	"ClaimPlace",
	"GoToZone",
	"GoToItem",
	"LeaveZone",
	"StartProduction",
	"StopProduction",
	"CollectProduction",
	"Despawn",
	"Stop",
	"Transform",
	"Teleport",
	"CreateTeam",
	"LeaveTeam",
	"InvitePlayerToTeam",
	"AcceptTeamInvite",
	"DeclineTeamInvite",
	"ChangeBehavior",
	"Interact",
	"Dialogue",
	"Spawn",
	"Notify",
	"SetAmbiance",
	"SetWorldProperty",
	"TweenWorldProperty",
	"SetGoal",
	"SetDailyQuest",
	"SetJournalQuest",
	"GiveQuest",
	"CheckQuestStatus",
	"ClaimQuest",
	"CheckoutQuest",
	"ClearQuestState",
	"Branch",
	"Compare",
	"WaitUntil",
	"Switch",
	"Choice",
	"PlaySound",
	"PlayAnimation",
	"ShowTitle",
	"OnProximity",
	"DisplayInterface",
	"HideInterface",
	"SetUiProperty",
	"TweenProperty",
	"ForEach",
	"ForEachPlayer",
	"AddChild",
	"Get",
	"Math",
	"Concat",
	"Filter",
	"Group",
	"Flatten",
	"ResolveItem",
	"TransformRecipeItem",
	"SelectChoice",
	"TransformFreeModeSlots",
	"DeliverQuestItems",
	"SelectTargetItem",
	"ClaimQuestById",
	"SetQuestGoal",
	"UseInventoryItem",
	"OpenSession",
	"Attack",
	"Countdown",
	"GetAllProductions",
	"CollectTimer",
	"SetState",
	"AppendState",
	"RemoveState",
] as const;

export type ActionType = (typeof ACTION_TYPES)[number];
export const ActionTypeSchema = z.enum(ACTION_TYPES);

export type NodeType = EventType | ActionType;

export const PositionSchema = z.object({
	x: z.number().finite(),
	y: z.number().finite(),
}).strict();

export const LogicalOperatorSchema = z.enum(["AND", "OR"]);

export const NodeInputBindingSchema = z.object({
	sourceNodeId: z.string().min(1),
	sourcePort: z.string().min(1),
}).strict();

export const NodeInputsSchema = z.record(z.string().min(1), NodeInputBindingSchema);

export type NodeInputBinding = z.infer<typeof NodeInputBindingSchema>;
export type NodeInputs = z.infer<typeof NodeInputsSchema>;

const NodeIdFields = {
	id: z.string().min(1),
	position: PositionSchema,
	parentId: z.string().min(1).optional(),
	inputs: NodeInputsSchema.optional(),
};

const hasInputBinding = (
	node: { inputs?: Record<string, unknown> },
	inputName: string,
): boolean => node.inputs?.[inputName] !== undefined;

const StatMode = z.enum(["Set", "Restore", "Increase", "Decrease"]);
const CurrencyMode = z.enum(["Add", "Remove", "Set"]);
const ItemMode = z.enum(["Add", "Remove", "Set"]);
const InteractAccess = z.enum(["Any", "Owner", "NonOwner"]);
const ProximityTargetKind = z.enum(["Players", "Items", "All"]);
const ProximityRelationship = z.enum(["Any", "Owner", "NonOwner"]);
const WorldPropertyKey = z.enum([
	"Width",
	"Height",
	"Depth",
	"Scale",
	"Position",
	"Rotation",
	"Visible",
	"Transparency",
	"CanCollide",
	"Anchored",
	"Color",
]);

const DialogueLineSchema = z.object({
	speaker: z.string().min(1),
	text: z.string().min(1),
});

const RandomItemEntrySchema = z.object({
	itemId: z.string().min(1, "itemId required"),
	chance: z.number().finite().nonnegative(),
	quantity: z.number().finite().int().positive().optional(),
	color: z
		.string()
		.regex(/^#[0-9a-fA-F]{6}$/, "color must be a 6-digit hex like #fde68a")
		.optional(),
});

const EntryPointNode = z.object({
	...NodeIdFields,
	type: EventTypeSchema,
});

const ModifyStatNode = z.object({
	...NodeIdFields,
	type: z.literal("ModifyStat"),
	mode: StatMode,
	statKey: z.string().min(1, "statKey required"),
	value: z.string().min(1, "value required"),
	target: z.string().optional(),
});

const ModifyCurrencyNode = z.object({
	...NodeIdFields,
	type: z.literal("ModifyCurrency"),
	mode: CurrencyMode,
	currencyId: z.string().min(1, "currencyId required"),
	value: z.string().min(1, "value required"),
	spawnDrop: z.boolean().optional(),
	target: z.string().optional(),
});

const SetTimerNode = z
	.object({
		...NodeIdFields,
		type: z.literal("SetTimer"),
		duration: z.number().finite().positive("duration must be > 0"),
		skipCurrencyId: z.string().min(1).optional(),
		skipAmount: z.number().finite().positive().optional(),
	})
	.superRefine((val, ctx) => {
		const hasId = val.skipCurrencyId !== undefined;
		const hasAmount = val.skipAmount !== undefined;
		if (hasId !== hasAmount) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "skipCurrencyId and skipAmount must be set together",
				path: [hasId ? "skipAmount" : "skipCurrencyId"],
			});
		}
	});

const ModifyItemNode = z
	.object({
		...NodeIdFields,
		type: z.literal("ModifyItem"),
		mode: ItemMode,
		itemId: z.string().min(1, "itemId required"),
		quantity: z.number().finite().int().nonnegative("quantity must be >= 0"),
		spawnDrop: z.boolean().optional(),
	})
	.superRefine((node, ctx) => {
		if (node.mode !== "Set" && node.quantity <= 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["quantity"],
				message: "quantity must be > 0 unless mode is Set",
			});
		}
		if (node.spawnDrop && node.mode !== "Add") {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["spawnDrop"],
				message: "spawnDrop is only supported when mode is Add",
			});
		}
	});

const AddRandomItemNode = z
	.object({
		...NodeIdFields,
		type: z.literal("AddRandomItem"),
		entries: z.array(RandomItemEntrySchema).min(1, "entries required"),
	})
	.superRefine((val, ctx) => {
		const total = val.entries.reduce((sum, e) => sum + (e.chance ?? 0), 0);
		if (total !== 10000) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["entries"],
				message: `Total chance must be 100% (got ${total / 100}%)`,
			});
		}
	});

const ExecuteTransactionNode = z.object({
	...NodeIdFields,
	type: z.literal("ExecuteTransaction"),
	recipeId: z.string().min(1, "recipeId required"),
});

const ConsumeItemNode = z.object({
	...NodeIdFields,
	type: z.literal("ConsumeItem"),
});

const ToggleEquipNode = z.object({
	...NodeIdFields,
	type: z.literal("ToggleEquip"),
});

const DeployItemNode = z.object({
	...NodeIdFields,
	type: z.literal("DeployItem"),
	target: z.string().optional(),
});

const RecallItemNode = z.object({
	...NodeIdFields,
	type: z.literal("RecallItem"),
	target: z.string().optional(),
});

const PlaceItemNode = z.object({
	...NodeIdFields,
	type: z.literal("PlaceItem"),
	target: z.string().optional(),
	filter: z.string().optional(),
	recipes: z.array(z.string().min(1)).min(1, "At least one recipe required"),
});

const ClaimPlaceNode = z.object({
	...NodeIdFields,
	type: z.literal("ClaimPlace"),
	placeGroupId: z.string().min(1, "placeGroupId required"),
});

const GoToZoneNode = z.object({
	...NodeIdFields,
	type: z.literal("GoToZone"),
	zoneItemId: z.string().min(1, "zoneItemId required"),
	placeGroupId: z.string().optional(),
	target: z.string().optional(),
});

const GoToItemNode = z.object({
	...NodeIdFields,
	type: z.literal("GoToItem"),
	itemId: z.string().min(1, "itemId required"),
	placeGroupId: z.string().optional(),
	target: z.string().optional(),
});

const LeaveZoneNode = z.object({
	...NodeIdFields,
	type: z.literal("LeaveZone"),
	target: z.string().optional(),
});

const ProductionOutputSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("item"),
		itemId: z.string().min(1, "itemId required"),
		ratePerHour: z.number().finite().nonnegative(),
		maxStoredHours: z.number().finite().positive().optional(),
	}),
	z.object({
		type: z.literal("currency"),
		currencyId: z.string().min(1, "currencyId required"),
		ratePerHour: z.number().finite().nonnegative(),
		maxStoredHours: z.number().finite().positive().optional(),
	}),
]);

const StartProductionNode = z.object({
	...NodeIdFields,
	type: z.literal("StartProduction"),
	target: z.string().optional(),
	outputs: z.array(ProductionOutputSchema).min(1, "At least one output required"),
});

const StopProductionNode = z.object({
	...NodeIdFields,
	type: z.literal("StopProduction"),
	target: z.string().optional(),
});

const CollectProductionNode = z.object({
	...NodeIdFields,
	type: z.literal("CollectProduction"),
	mode: z.enum(["Self", "All", "Target"]).default("Self"),
	target: z.string().optional(),
});

const DespawnNode = z.object({
	...NodeIdFields,
	type: z.literal("Despawn"),
});

const StopNode = z.object({
	...NodeIdFields,
	type: z.literal("Stop"),
});

const TransformNode = z.object({
	...NodeIdFields,
	type: z.literal("Transform"),
	recipes: z.array(z.string().min(1)).min(1, "At least one recipe required"),
	title: z.string().min(1, "title required"),
	allowFreeMode: z.boolean().optional(),
	button: z.string().optional(),
});

const TeleportNode = z.object({
	...NodeIdFields,
	type: z.literal("Teleport"),
	destination: z.string().optional(),
});

const CreateTeamNode = z.object({
	...NodeIdFields,
	type: z.literal("CreateTeam"),
	maxSize: z.number().int().positive().default(2),
});

const LeaveTeamNode = z.object({
	...NodeIdFields,
	type: z.literal("LeaveTeam"),
});

const InvitePlayerToTeamNode = z
	.object({
		...NodeIdFields,
		type: z.literal("InvitePlayerToTeam"),
		targetUserId: z
			.number()
			.int()
			.refine((value) => value !== 0, "targetUserId must not be 0")
			.optional(),
		maxSize: z.number().int().positive().default(2),
	})
	.superRefine((node, ctx) => {
		if (node.targetUserId === undefined && !hasInputBinding(node, "targetUserId")) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["targetUserId"],
				message: "targetUserId required (literal or data input)",
			});
		}
	});

const AcceptTeamInviteNode = z
	.object({
		...NodeIdFields,
		type: z.literal("AcceptTeamInvite"),
		inviteId: z.string().min(1).optional(),
	})
	.superRefine((node, ctx) => {
		if (node.inviteId === undefined && !hasInputBinding(node, "inviteId")) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["inviteId"],
				message: "inviteId required (literal or data input)",
			});
		}
	});

const DeclineTeamInviteNode = z
	.object({
		...NodeIdFields,
		type: z.literal("DeclineTeamInvite"),
		inviteId: z.string().min(1).optional(),
	})
	.superRefine((node, ctx) => {
		if (node.inviteId === undefined && !hasInputBinding(node, "inviteId")) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["inviteId"],
				message: "inviteId required (literal or data input)",
			});
		}
	});

const OnProximityNode = z.object({
	...NodeIdFields,
	type: z.literal("OnProximity"),
	radius: z.number().finite().nonnegative("radius must be >= 0"),
	targetKind: ProximityTargetKind.default("Players"),
	relationship: ProximityRelationship.default("Any"),
	itemIds: z.array(z.string().min(1)).optional(),
});

const BehaviorTypeSchema = z.enum(["Static", "Move", "Protect", "Aggressive"]);

const ChangeBehaviorNode = z
	.object({
		...NodeIdFields,
		type: z.literal("ChangeBehavior"),
		behaviorType: BehaviorTypeSchema,
		stats: z.record(z.string().min(1), z.number().finite()).optional(),
		attacks: z.array(z.string()).optional(),
		targetItemId: z.string().min(1).optional(),
		targetItemIds: z.array(z.string().min(1)).optional(),
	})
		.superRefine((node, ctx) => {
			if (node.behaviorType === "Protect" && !node.targetItemId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["targetItemId"],
					message: "targetItemId required for Protect behavior",
				});
			}
			if (node.behaviorType !== "Protect" && node.targetItemId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["targetItemId"],
					message: "targetItemId is reserved for Protect behavior",
				});
			}
			if (node.behaviorType !== "Aggressive" && node.targetItemIds && node.targetItemIds.length > 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["targetItemIds"],
					message: "targetItemIds is reserved for Aggressive behavior",
				});
			}
		});

const InteractNode = z.object({
	...NodeIdFields,
	type: z.literal("Interact"),
	label: z.string().min(1, "label required"),
	holdDuration: z.number().finite().nonnegative().optional(),
	access: InteractAccess.default("Any"),
});

const DialogueNode = z.object({
	...NodeIdFields,
	type: z.literal("Dialogue"),
	lines: z.array(DialogueLineSchema).min(1, "At least one line required"),
});

const SpawnNode = z.object({
	...NodeIdFields,
	type: z.literal("Spawn"),
	spawnType: z.enum(["respawn", "once"]),
	maxActive: z.number().finite().int().positive(),
	interval: z.number().finite().nonnegative(),
	contentList: z.array(z.string()).min(1),
});

const NotifyNode = z.object({
	...NodeIdFields,
	type: z.literal("Notify"),
	notifType: z.string().min(1, "notifType required"),
	message: z.string().min(1, "message required"),
});

const SetAmbianceNode = z.object({
	...NodeIdFields,
	type: z.literal("SetAmbiance"),
	preset: z.string().min(1, "preset required"),
	transitionDuration: z.number().finite().nonnegative().optional(),
});

const WorldVectorSchema = z.object({
	x: z.number().finite(),
	y: z.number().finite(),
	z: z.number().finite(),
});

const WorldColorSchema = z.object({
	r: z.number().int().min(0).max(255),
	g: z.number().int().min(0).max(255),
	b: z.number().int().min(0).max(255),
});

const TweenEasingSchema = z.enum([
	"linear",
	"quad-out",
	"quad-in-out",
	"back-out",
	"elastic-out",
]);
export type TweenEasing = z.infer<typeof TweenEasingSchema>;

const SetWorldPropertyNode = z
	.object({
		...NodeIdFields,
		type: z.literal("SetWorldProperty"),
		target: z.string().optional(),
		propertyKey: WorldPropertyKey,
		value: z.union([z.string(), z.number(), z.boolean(), WorldVectorSchema, WorldColorSchema]).optional(),
	})
	.superRefine((node, ctx) => {
		if (node.value === undefined && node.inputs?.value === undefined) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["value"],
				message: "value required (literal or inputs.value binding)",
			});
		}
	});

const TweenWorldPropertyNode = z
	.object({
		...NodeIdFields,
		type: z.literal("TweenWorldProperty"),
		target: z.string().optional(),
		propertyKey: WorldPropertyKey,
		duration: z.number().finite().min(0).max(60).default(1),
		delay: z.number().finite().min(0).max(60).default(0),
		easing: TweenEasingSchema.default("quad-out"),
		value: z.union([z.string(), z.number(), z.boolean(), WorldVectorSchema, WorldColorSchema]).optional(),
	})
	.superRefine((node, ctx) => {
		if (node.value === undefined && node.inputs?.value === undefined) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["value"],
				message: "value required (literal or inputs.value binding)",
			});
		}
	});

const SetGoalNode = z.object({
	...NodeIdFields,
	type: z.literal("SetGoal"),
	targetItemId: z.string().min(1, "targetItemId required"),
});

const SetDailyQuestNode = z.object({
	...NodeIdFields,
	type: z.literal("SetDailyQuest"),
});

const SetJournalQuestNode = z.object({
	...NodeIdFields,
	type: z.literal("SetJournalQuest"),
});

const GiveQuestNode = z.object({
	...NodeIdFields,
	type: z.literal("GiveQuest"),
	questIds: z.array(z.string().min(1)).min(1, "At least one questId required"),
});

const CheckQuestStatusNode = z.object({
	...NodeIdFields,
	type: z.literal("CheckQuestStatus"),
	questIds: z.array(z.string().min(1)).min(1, "At least one questId required"),
});

const ClaimQuestNode = z.object({
	...NodeIdFields,
	type: z.literal("ClaimQuest"),
});

const CheckoutQuestNode = z.object({
	...NodeIdFields,
	type: z.literal("CheckoutQuest"),
});

const ClearQuestStateNode = z.object({
	...NodeIdFields,
	type: z.literal("ClearQuestState"),
});

const BranchNode = z.object({
	...NodeIdFields,
	type: z.literal("Branch"),
	logicalOperator: LogicalOperatorSchema.optional(),
	conditions: z.array(ConditionSchema).optional(),
});

const WaitUntilNode = z.object({
	...NodeIdFields,
	type: z.literal("WaitUntil"),
	logicalOperator: LogicalOperatorSchema,
	conditions: z.array(ConditionSchema).min(1, "At least one condition required"),
	timeoutSeconds: z.number().positive().optional(),
});

export const SwitchCaseSchema = z.object({
	value: z.union([z.string(), z.number(), z.boolean()]),
});

const SwitchNode = z
	.object({
		...NodeIdFields,
		type: z.literal("Switch"),
		cases: z.array(SwitchCaseSchema).min(1, "At least one case required"),
	})
	.superRefine((node, ctx) => {
		const seenCases = new Map<string, number>();
		node.cases.forEach((switchCase, index) => {
			const key = `${typeof switchCase.value}:${String(switchCase.value)}`;
			const previous = seenCases.get(key);
			if (previous !== undefined) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["cases", index, "value"],
					message: `Duplicate Switch case value also used at index ${previous}`,
				});
			}
			seenCases.set(key, index);
		});
	});

export const ChoiceCaseSchema = z.object({
	label: z.string().min(1, "label required"),
	handle: z.string().min(1, "handle required"),
});

const ChoiceNode = z
	.object({
		...NodeIdFields,
		type: z.literal("Choice"),
		prompt: z.string().min(1, "prompt required"),
		cases: z.array(ChoiceCaseSchema).min(1, "At least one case required"),
		speaker: z.enum(["Player", "Entity"]).optional(),
	})
	.superRefine((val, ctx) => {
		const seenHandles = new Set<string>();
		val.cases.forEach((c, idx) => {
			if (c.handle === "Default") {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["cases", idx, "handle"],
					message: '"Default" handle is reserved',
				});
			}
			if (seenHandles.has(c.handle)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["cases", idx, "handle"],
					message: `Duplicate handle "${c.handle}"`,
				});
			}
			seenHandles.add(c.handle);
		});
	});

const PlaySoundNode = z.object({
	...NodeIdFields,
	type: z.literal("PlaySound"),
	soundPath: z.string().nullable().optional(),
});

const PlayAnimationNode = z.object({
	...NodeIdFields,
	type: z.literal("PlayAnimation"),
	target: z.string().default("Entity"),
	animationPath: z.string().nullable().optional(),
});

const ShowTitleNode = z.object({
	...NodeIdFields,
	type: z.literal("ShowTitle"),
	text: z.string().min(1, "text required"),
});

const DisplayInterfaceNode = z
	.object({
		...NodeIdFields,
		type: z.literal("DisplayInterface"),
		interfaceName: z.string().optional(),
	})
	.superRefine((node, ctx) => {
		const hasLiteral = typeof node.interfaceName === "string" && node.interfaceName.length > 0;
		const hasBinding = node.inputs?.interfaceName !== undefined;
		if (!hasLiteral && !hasBinding) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["interfaceName"],
				message: "interfaceName required (either as literal or as inputs.interfaceName binding)",
			});
		}
	});

const HideInterfaceNode = z
	.object({
		...NodeIdFields,
		type: z.literal("HideInterface"),
		interfaceName: z.string().optional(),
	})
	.superRefine((node, ctx) => {
		const hasLiteral = typeof node.interfaceName === "string" && node.interfaceName.length > 0;
		const hasBinding = node.inputs?.interfaceName !== undefined;
		if (!hasLiteral && !hasBinding) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["interfaceName"],
				message: "interfaceName required (either as literal or as inputs.interfaceName binding)",
			});
		}
	});

const UiColorRgbSchema = z.object({
	r: z.number().int().min(0).max(255),
	g: z.number().int().min(0).max(255),
	b: z.number().int().min(0).max(255),
});

const SetUiPropertyNode = z
	.object({
		...NodeIdFields,
		type: z.literal("SetUiProperty"),
		nodeId: z.string().min(1, "nodeId required"),
		propertyKey: z.string().min(1, "propertyKey required").transform(normalizeUiPropertyKey),

		value: z
			.union([
				z.string(),
				z.number(),
				z.boolean(),
				UiColorRgbSchema,
				z.object({ token: z.string().min(1) }).strict(),
			])
			.optional(),
	})
	.superRefine((val, ctx) => {
		if (!isValidUiPropertyKey(val.propertyKey)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["propertyKey"],
				message: `Unknown UI property '${val.propertyKey}'. Valid: ${UI_PROPERTY_KEYS.join(", ")}`,
			});
		}
	});

const TweenPropertyNode = z
	.object({
		...NodeIdFields,
		type: z.literal("TweenProperty"),
		nodeId: z.string().min(1, "nodeId required"),
		propertyKey: z.string().min(1, "propertyKey required").transform(normalizeUiPropertyKey),
		duration: z.number().finite().min(0).max(60000).default(300),
		delay: z.number().finite().min(0).max(60000).default(0),
		easing: TweenEasingSchema.default("quad-out"),

		value: z
			.union([
				z.string(),
				z.number(),
				z.boolean(),
				UiColorRgbSchema,
				z.object({ token: z.string().min(1) }).strict(),
			])
			.optional(),
	})
	.superRefine((val, ctx) => {
		if (!isValidUiPropertyKey(val.propertyKey)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["propertyKey"],
				message: `Unknown UI property '${val.propertyKey}'. Valid: ${UI_PROPERTY_KEYS.join(", ")}`,
			});
		}
	});

import { GET_SOURCES, getSourceSpec } from "./getRegistry.ts";
import { MATH_OPS } from "./mathRegistry.ts";
import { COMPARE_OPS } from "./compareRegistry.ts";
import { CONTEXT_SCOPES, getContextScopeSpec } from "./contextRegistry.ts";

const GetQuerySchema = z.object({
	source: z.string().min(1),
	arg: z.string().optional(),
});

const GetNode = z
	.object({
		...NodeIdFields,
		type: z.literal("Get"),
		query: GetQuerySchema,
	})
	.superRefine((node, ctx) => {
		const q = node.query;
		const spec = getSourceSpec(q.source);
		if (!spec) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["query", "source"],
				message: `Unknown Get source '${q.source}'. Valid: ${GET_SOURCES.join(", ")}`,
			});
			return;
		}
		const hasLiteralArg = typeof q.arg === "string" && q.arg.length > 0;
		const hasBoundArg = node.inputs?.arg !== undefined;
		if (spec.paramKind && !hasLiteralArg && !hasBoundArg) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["query", "arg"],
				message: `Source '${q.source}' requires an arg of kind '${spec.paramKind}' (literal query.arg or inputs.arg binding)`,
			});
		}
		if (!spec.paramKind && (hasLiteralArg || hasBoundArg)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["query", "arg"],
				message: `Source '${q.source}' does not accept an arg`,
			});
		}

		if (spec.paramKind === "scope" && hasLiteralArg) {

			if (q.source !== "State" && !getContextScopeSpec(q.arg as string)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["query", "arg"],
					message: `Unknown Context scope '${q.arg}'. Valid: ${CONTEXT_SCOPES.join(", ")}`,
				});
			}
		}
	});

const MathOperatorSchema = z.string().refine((op) => MATH_OPS.includes(op), {
	message: `operator must be one of ${MATH_OPS.join(", ")}`,
});
const MathNode = z.object({
	...NodeIdFields,
	type: z.literal("Math"),
	operator: MathOperatorSchema,
	a: z.number().optional(),
	b: z.number().optional(),
});

const CompareOperatorSchema = z.string().refine(
	(op) => COMPARE_OPS.includes(op),
	{ message: `operator must be one of ${COMPARE_OPS.join(", ")}` },
);
const CompareLiteral = z.union([z.string(), z.number(), z.boolean()]).optional();
const CompareNode = z.object({
	...NodeIdFields,
	type: z.literal("Compare"),
	operator: CompareOperatorSchema,
	a: CompareLiteral,
	b: CompareLiteral,
});

const ConcatPartSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("literal"), value: z.string() }),
	z.object({ kind: z.literal("edge") }),
]);

const ConcatNode = z.object({
	...NodeIdFields,
	type: z.literal("Concat"),
	parts: z.array(ConcatPartSchema).min(1, "At least one part required"),
});

export const FILTER_OPS = ["==", "!=", ">=", "<=", ">", "<"] as const;
const FilterOperatorSchema = z.enum(FILTER_OPS);
const FilterNode = z.object({
	...NodeIdFields,
	type: z.literal("Filter"),
	field: z.string(),
	op: FilterOperatorSchema,
	value: z.union([z.string(), z.number(), z.boolean()]).optional(),
});

const GroupNode = z.object({
	...NodeIdFields,
	type: z.literal("Group"),
	by: z.string().optional(),
});

const FlattenNode = z.object({
	...NodeIdFields,
	type: z.literal("Flatten"),
	by: z.string().optional(),
});

const ResolveItemNode = z.object({
	...NodeIdFields,
	type: z.literal("ResolveItem"),
});

const ForEachNode = z.object({
	...NodeIdFields,
	type: z.literal("ForEach"),
	list: z.array(z.unknown()).optional(),
	virtualized: z.boolean().optional(),
	virtualItemHeight: z.number().finite().positive().optional(),
	virtualItemWidth: z.number().finite().positive().optional(),
	virtualBuffer: z.number().finite().int().nonnegative().max(200).optional(),
});

const ForEachPlayerNode = z.object({
	...NodeIdFields,
	type: z.literal("ForEachPlayer"),
	scope: z.enum(["AllConnected", "PlaceOwners", "MatchPlayers"]).default("PlaceOwners"),
});

const AddChildNode = z.object({
	...NodeIdFields,
	type: z.literal("AddChild"),
	parentNodeId: z.string().min(1),
	templateName: z.string().min(1),
	scope: z.string().optional(),
});

const TransformRecipeItemNode = z.object({
	...NodeIdFields,
	type: z.literal("TransformRecipeItem"),
});

const SelectChoiceNode = z.object({
	...NodeIdFields,
	type: z.literal("SelectChoice"),
});

const TransformFreeModeSlotsNode = z.object({
	...NodeIdFields,
	type: z.literal("TransformFreeModeSlots"),
	inputs: z.object({ slots: NodeInputBindingSchema }).optional(),
});

const DeliverQuestItemsNode = z.object({
	...NodeIdFields,
	type: z.literal("DeliverQuestItems"),
});

const SelectTargetItemNode = z.object({
	...NodeIdFields,
	type: z.literal("SelectTargetItem"),
});

const ClaimQuestByIdNode = z.object({
	...NodeIdFields,
	type: z.literal("ClaimQuestById"),
});

const SetQuestGoalNode = z.object({
	...NodeIdFields,
	type: z.literal("SetQuestGoal"),
});

const UseInventoryItemNode = z.object({
	...NodeIdFields,
	type: z.literal("UseInventoryItem"),
});

const OpenSessionNode = z.object({
	...NodeIdFields,
	type: z.literal("OpenSession"),
	sessionName: z.string().min(1, "sessionName required"),
});

const AttackNode = z.object({
	...NodeIdFields,
	type: z.literal("Attack"),
});

const CountdownNode = z.object({
	...NodeIdFields,
	type: z.literal("Countdown"),
});

const GetAllProductionsNode = z.object({
	...NodeIdFields,
	type: z.literal("GetAllProductions"),
});

const CollectTimerNode = z.object({
	...NodeIdFields,
	type: z.literal("CollectTimer"),
});

const StateValueLiteral = z.union([z.string(), z.number(), z.boolean()]).optional();

const refineStateKey = (
	node: { stateKey?: string; inputs?: Record<string, unknown> },
	ctx: z.RefinementCtx,
) => {
	const hasLiteral = typeof node.stateKey === "string" && node.stateKey.length > 0;
	const hasBinding = node.inputs?.stateKey !== undefined;
	if (!hasLiteral && !hasBinding) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ["stateKey"],
			message: "stateKey required (either as literal or as inputs.stateKey binding)",
		});
	}
};

const SetStateNode = z
	.object({
		...NodeIdFields,
		type: z.literal("SetState"),
		stateKey: z.string().optional(),
		value: StateValueLiteral,
	})
	.superRefine(refineStateKey);

const AppendStateNode = z
	.object({
		...NodeIdFields,
		type: z.literal("AppendState"),
		stateKey: z.string().optional(),
		value: StateValueLiteral,
	})
	.superRefine(refineStateKey);

const RemoveStateNode = z
	.object({
		...NodeIdFields,
		type: z.literal("RemoveState"),
		stateKey: z.string().optional(),
		value: StateValueLiteral,
	})
	.superRefine(refineStateKey);

export const NodeSchema = z.union([
	EntryPointNode,
	ModifyStatNode,
	ModifyCurrencyNode,
	SetTimerNode,
	ModifyItemNode,
	AddRandomItemNode,
	ExecuteTransactionNode,
	ConsumeItemNode,
	ToggleEquipNode,
	DeployItemNode,
	RecallItemNode,
	PlaceItemNode,
	ClaimPlaceNode,
	GoToZoneNode,
	GoToItemNode,
	LeaveZoneNode,
	StartProductionNode,
	StopProductionNode,
	CollectProductionNode,
	DespawnNode,
	StopNode,
	TransformNode,
	TeleportNode,
	CreateTeamNode,
	LeaveTeamNode,
	InvitePlayerToTeamNode,
	AcceptTeamInviteNode,
	DeclineTeamInviteNode,
	ChangeBehaviorNode,
	InteractNode,
	DialogueNode,
	SpawnNode,
	NotifyNode,
	SetAmbianceNode,
	SetWorldPropertyNode,
	TweenWorldPropertyNode,
	SetGoalNode,
	SetDailyQuestNode,
	SetJournalQuestNode,
	GiveQuestNode,
	CheckQuestStatusNode,
	ClaimQuestNode,
	CheckoutQuestNode,
	ClearQuestStateNode,
	BranchNode,
	WaitUntilNode,
	SwitchNode,
	ChoiceNode,
	PlaySoundNode,
	PlayAnimationNode,
	ShowTitleNode,
	OnProximityNode,
	DisplayInterfaceNode,
	HideInterfaceNode,
	SetUiPropertyNode,
	TweenPropertyNode,
	GetNode,
	MathNode,
	CompareNode,
	ConcatNode,
	FilterNode,
	GroupNode,
	FlattenNode,
	ResolveItemNode,
	ForEachNode,
	ForEachPlayerNode,
	AddChildNode,
	TransformRecipeItemNode,
	SelectChoiceNode,
	TransformFreeModeSlotsNode,
	DeliverQuestItemsNode,
	SelectTargetItemNode,
	ClaimQuestByIdNode,
	SetQuestGoalNode,
	UseInventoryItemNode,
	OpenSessionNode,
	AttackNode,
	CountdownNode,
	GetAllProductionsNode,
	CollectTimerNode,
	SetStateNode,
	AppendStateNode,
	RemoveStateNode,
]);

export type ActionNode = z.infer<typeof NodeSchema>;

export const getNodeInputs = (node: ActionNode): NodeInputs | undefined =>
	node.inputs;

export const EdgeSchema = z.object({
	id: z.string().min(1),
	source: z.string().min(1),
	sourceHandle: z.string().min(1),
	target: z.string().min(1),
}).strict();

export type ActionEdge = z.infer<typeof EdgeSchema>;

const migrateLegacyNode = (node: unknown): unknown => {
	if (!node || typeof node !== "object" || Array.isArray(node)) return node;
	const raw = node as Record<string, unknown>;
	if (raw.type !== "AddItem") return node;
	const quantity = typeof raw.quantity === "number" ? raw.quantity : 1;
	const migrated: Record<string, unknown> = {
		id: raw.id,
		type: "ModifyItem",
		position: raw.position,
		mode: "Add",
		itemId: raw.itemId,
		quantity,
	};
	if (raw.parentId !== undefined) migrated.parentId = raw.parentId;
	if (raw.inputs !== undefined) migrated.inputs = raw.inputs;
	if (raw.spawnDrop !== undefined) migrated.spawnDrop = raw.spawnDrop;
	return migrated;
};

export const migrateLegacyGraphActions = (graph: unknown): unknown => {
	if (!graph || typeof graph !== "object" || Array.isArray(graph)) return graph;
	const raw = graph as Record<string, unknown>;
	if (!raw.nodes || typeof raw.nodes !== "object" || Array.isArray(raw.nodes)) {
		return graph;
	}
	const nodes: Record<string, unknown> = {};
	for (const [nodeId, node] of Object.entries(raw.nodes)) {
		nodes[nodeId] = migrateLegacyNode(node);
	}
	return { ...raw, nodes };
};

export const CommentSchema = z
	.object({
		id: z.string().min(1),
		text: z.string().max(COMMENT_TEXT_MAX_LENGTH),
		color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "color must be a 6-digit hex like #fde68a"),
		position: z
			.object({
				x: z.number().finite(),
				y: z.number().finite(),
			})
			.strict(),
		size: z
			.object({
				width: z.number().finite().min(80).max(COMMENT_SIZE_MAX),
				height: z.number().finite().min(48).max(COMMENT_SIZE_MAX),
			})
			.strict(),
	})
	.strict();

export type Comment = z.infer<typeof CommentSchema>;

const EVENT_TYPE_SET = new Set<string>(EVENT_TYPES);

export const GraphSchema = z.preprocess(migrateLegacyGraphActions, z
	.object({
		nodes: z.record(z.string().min(1), NodeSchema),
		edges: z.array(EdgeSchema),
		comments: z.array(CommentSchema).optional(),
	})
	.superRefine((graph, ctx) => {

		for (const [key, node] of Object.entries(graph.nodes)) {
			if (node.id !== key) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["nodes", key, "id"],
					message: `nodes["${key}"].id must equal "${key}" (got "${node.id}")`,
				});
			}
		}

		const seenEdgeIds = new Set<string>();
		graph.edges.forEach((edge, idx) => {
			if (seenEdgeIds.has(edge.id)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["edges", idx, "id"],
					message: `Duplicate edge id "${edge.id}"`,
				});
			}
			seenEdgeIds.add(edge.id);

			if (!graph.nodes[edge.source]) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["edges", idx, "source"],
					message: `source node "${edge.source}" does not exist`,
				});
			}

			const targetNode = graph.nodes[edge.target];
			if (!targetNode) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["edges", idx, "target"],
					message: `target node "${edge.target}" does not exist`,
				});
			} else if (EVENT_TYPE_SET.has(targetNode.type)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["edges", idx, "target"],
					message: `EntryPoint node "${edge.target}" cannot receive incoming edges`,
				});
			}
		});

		for (const [nodeId, node] of Object.entries(graph.nodes)) {
			const inputs = getNodeInputs(node);
			if (!inputs) continue;
			const entries = Object.entries(inputs);
			if (entries.length === 0) continue;
			if (EVENT_TYPE_SET.has(node.type)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["nodes", nodeId, "inputs"],
					message: `EntryPoint node "${nodeId}" cannot declare data inputs`,
				});
				continue;
			}
			for (const [port, binding] of entries) {
				if (!graph.nodes[binding.sourceNodeId]) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ["nodes", nodeId, "inputs", port, "sourceNodeId"],
						message: `Data binding on '${nodeId}.${port}' references unknown source node "${binding.sourceNodeId}"`,
					});
				}
			}
		}
	}));

export type ActionGraph = z.infer<typeof GraphSchema>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

export const parseActionGraph = (graph: unknown): ActionGraph | null => {
	if (graph === null || graph === undefined) return null;
	const migrated = migrateLegacyGraphActions(graph);
	if (!isRecord(migrated)) return null;
	const nodes = migrated.nodes;
	const edges = migrated.edges;
	if (!isRecord(nodes) || !Array.isArray(edges)) return null;
	const result: ActionGraph = {
		nodes: nodes as ActionGraph["nodes"],
		edges: edges as ActionGraph["edges"],
	};
	if (Array.isArray(migrated.comments)) {
		result.comments = migrated.comments as ActionGraph["comments"];
	}
	return result;
};
